<template>
    <main>
        <h1>{{ pageTitle }}</h1>
        <BaseSection>
            <BaseCardCollection
                v-if="pending || (cards && cards.length)"
                :cards="cards || []"
                :wide="true"
                :placeholder="pending"
            />
            <p v-else>Sorry, there are no news articles available.</p>
        </BaseSection>
        <BaseSection>
            <BasePaginator
                :current-page="currentPage"
                :total-pages="Math.ceil(total / DEFAULT_LIMIT)"
                @update:current-page="currentPage = $event"
            />
        </BaseSection>
    </main>
</template>

<script setup lang="ts">
import OverviewMetaQuery from '~/graphql/queries/news/OverviewMeta.graphql';
import NewsListQuery from '~/graphql/queries/news/NewsList.graphql';
import NewsByIdsQuery from '~/graphql/queries/news/NewsByIds.graphql';

import useSrcSets from '~/utils/srcset';

const { activeSite } = useMultisite();
const { getSrcSet } = useSrcSets();

definePageMeta({
    middleware: 'news-routing'
});

const { variables } = useCraftVariables();
const DEFAULT_LIMIT = 16;
variables.uri = undefined;
variables.limit = DEFAULT_LIMIT;

// Setup pagination effect
const route = useRoute();
const currentPage = useCurrentPage();

watchEffect(() => {
    const page = Math.max(1, Number(route.query.page) || 1);
    variables.offset = (page - 1) * variables.limit;
});

type ListEntry = {
    id: string | number;
    postDate: string;
    siteHandle: string;
    isCrossPublished: boolean;
    crossPublicationCategories?: Array<{ id: string | number }>;
};

// 1. Overview meta: SEO + the cross-publication categories configured on this site
const overviewResponse = await useLazyAsyncQuery({
    query: OverviewMetaQuery,
    variables
});

const categoryIds = computed<Array<string | number>>(() =>
    overviewResponse.data.value?.overview?.overviewNewsCategory?.map((cat: any) => cat.id) ?? []
);

// 2. Lightweight list (no image transforms): the full ordering source used to
// paginate client-side. Own news + cross-published news from sibling sites.
const listResponse = await useLazyAsyncQuery({
    query: NewsListQuery,
    variables: computed(() => ({
        site: variables.site,
        relatedTo: categoryIds.value.map(Number),
        includeCross: categoryIds.value.length > 0
    })),
    immediate: true,
    watch: categoryIds
});

const orderedList = computed<ListEntry[]>(() => {
    const own: ListEntry[] = (listResponse.data.value?.own ?? []).map((entry: any) => ({
        ...entry,
        isCrossPublished: false
    }));

    const cross: ListEntry[] = (listResponse.data.value?.cross ?? [])
        .filter((entry: any) =>
            entry.crossPublicationCategories?.some((cat: { id: string | number }) =>
                categoryIds.value.includes(cat.id))
        )
        .map((entry: any) => ({
            ...entry,
            isCrossPublished: entry.siteHandle !== activeSite.value?.handle
        }));

    return [...own, ...cross]
        .filter((item, index, self) => index === self.findIndex(t => t.id === item.id))
        .sort((a, b) => new Date(b.postDate).getTime() - new Date(a.postDate).getTime());
});

const total = computed(() => orderedList.value.length);

const pageIds = computed(() =>
    orderedList.value
        .slice(variables.offset, variables.offset + variables.limit)
        .map(entry => entry.id)
);

// 3. Detail query: full data (incl. images) for only the visible page's items
const detailResponse = await useLazyAsyncQuery({
    query: NewsByIdsQuery,
    variables: computed(() => ({ ids: pageIds.value })),
    immediate: pageIds.value.length > 0,
    watch: pageIds
});

const isPatients = computed(() => activeSite?.group === 'patients');

const title = computed(() => (
    overviewResponse.data.value?.overview?.seoOverviewNewsTitle ??
    'News'
));

const description = computed(() => (
    overviewResponse.data.value?.overview?.seoOverviewNewsDescription ??
    activeSite.value?.seo?.description
));

const pageTitle = computed(() => isPatients.value ? 'Updates' : (title.value || 'News'));

usePageQuery(
    detailResponse,
    {
        entryKey: 'entries',
        seoTitle: title.value,
        seoDescription: description.value,
        activeSite
    }
);

// Loading until the visible page's detail data is in
const pending = computed(() =>
    listResponse.pending.value ||
    (pageIds.value.length > 0 && detailResponse.pending.value)
);

const { formatSingleDate, formats } = useDate();
const { getCardPills } = usePillsCard();

// Detail entries keyed by id so we can re-apply the merged order
const detailById = computed(() => {
    const map = new Map<string | number, any>();
    (detailResponse.data.value?.entries ?? []).forEach((entry: any) => map.set(entry.id, entry));
    return map;
});

const cards = computed(() => {
    if (!pageIds.value.length) {
        return null;
    }

    return pageIds.value
        .map((id) => {
            const news = detailById.value.get(id);
            if (!news) {
                return null;
            }

            const listEntry = orderedList.value.find(entry => entry.id === id);

            let image = null;
            if (news.image?.length) {
                image = {
                    src: news.image[0].large,
                    srcset: getSrcSet(news.image[0])
                };
            }

            return {
                image,
                title: news.title,
                meta: formatSingleDate(
                    news.postDate,
                    formats.dayMonthYear
                ),
                uri: news.uri,
                url: news.url,
                pills: isPatients.value
                    ? [{ text: 'Update' }]
                    : getCardPills(news),

                isCrossPublished: listEntry?.isCrossPublished
                    ?? (news.siteHandle !== activeSite.value?.handle),
                handle: news.siteHandle
            };
        })
        .filter(Boolean);
});

watch(() => currentPage.value, () => {
    window.scrollTo(0, 0);
});
</script>
